import { ArrowLeftIcon, ChevronDownIcon } from 'lucide-react'
import { useState } from 'react'

import { Sheet, SheetContent, SheetDescription, SheetTitle } from 'components/ui/sheet'
import employersEmployees from 'rails_routes/Employers/EmployeesPaths'
import { useBreakpoint } from 'slab'

import { BulkOffboardingProvider, type OffboardingStep, useBulkOffboarding } from './BulkOffboardingContext'
import { OffboardingDetailsStep } from './OffboardingDetailsStep'
import { OffboardingSidebar } from './OffboardingSidebar'
import { SelectEmployeesStep } from './SelectEmployeesStep'
import { SubmitStep } from './SubmitStep'

interface BulkOffboardingPageProps {
  isConnectedToPayroll: boolean
  employerName?: string | null
  employerDba?: string | null
  employeeId?: string | null
  selectedEmployeeIds?: string[] | null
  showNavigation?: boolean
  isAle?: boolean | null
}

function MobileTopBar ({ onOpenNav }: { onOpenNav: () => void }) {
  const { currentStep } = useBulkOffboarding()

  const getStepInfo = () => {
    switch (currentStep) {
      case 'select':
        return { current: 1, total: 3, label: 'Select employees' }
      case 'details':
        return { current: 2, total: 3, label: 'Offboarding details' }
      case 'submit':
        return { current: 3, total: 3, label: 'Summary' }
      default:
        return { current: 1, total: 3, label: '' }
    }
  }

  const stepInfo = getStepInfo()

  return (
    <div className='sticky top-0 z-10 flex h-14 items-center justify-between border-b border-gray-200 bg-white px-4'>
      <a
        href={employersEmployees.index()}
        className='flex items-center gap-2 text-gray-500 hover:text-gray-700 text-sm font-medium'
      >
        <ArrowLeftIcon size={16} />
        <span>Back to home</span>
      </a>

      <button
        onClick={onOpenNav}
        className='flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900'
      >
        <span>Step {stepInfo.current}/{stepInfo.total}</span>
        <ChevronDownIcon size={16} />
      </button>
    </div>
  )
}

function BulkOffboardingContent ({ employerName, employerDba, showNavigation = true, isAle }: { employerName?: string | null, employerDba?: string | null, showNavigation?: boolean, isAle?: boolean | null }) {
  const {
    currentStep,
    setCurrentStep,
    setCurrentEmployeeIndex,
  } = useBulkOffboarding()

  const { breakpointIsAtLeast } = useBreakpoint()
  const [navOpen, setNavOpen] = useState(false)
  const isDesktop = breakpointIsAtLeast('md')

  const handleStepClick = (step: OffboardingStep) => {
    if (step === 'select') {
      setCurrentStep('select')
    }
    setNavOpen(false)
  }

  const handleEmployeeClick = (index: number) => {
    setCurrentEmployeeIndex(index)
    setNavOpen(false)
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'select':
        return <SelectEmployeesStep />
      case 'details':
        return <OffboardingDetailsStep employerName={employerName} employerDba={employerDba} isAle={isAle} />
      case 'submit':
        return <SubmitStep employerName={employerName} employerDba={employerDba} isAle={isAle} />
      default:
        return null
    }
  }

  // When showNavigation is false, we're inside the Rails layout - just render content
  if (!showNavigation) {
    return renderStepContent()
  }

  return (
    <div className='flex flex-col min-h-svh bg-gray-50'>
      {/* Mobile top bar */}
      {!isDesktop && (
        <MobileTopBar onOpenNav={() => setNavOpen(true)} />
      )}

      {/* Mobile nav sheet */}
      <Sheet open={navOpen} onOpenChange={setNavOpen}>
        <SheetContent side='left' className='w-72 p-0'>
          <SheetTitle className='sr-only'>Navigation</SheetTitle>
          <SheetDescription className='sr-only'>
            Navigate through the offboarding steps
          </SheetDescription>
          <OffboardingSidebar
            onStepClick={handleStepClick}
            onEmployeeClick={handleEmployeeClick}
          />
        </SheetContent>
      </Sheet>

      <div className='flex flex-1'>
        {/* Desktop sidebar */}
        {isDesktop && (
          <div className='w-72 min-w-72 bg-gray-100 border-r border-gray-200'>
            <OffboardingSidebar
              onStepClick={handleStepClick}
              onEmployeeClick={handleEmployeeClick}
            />
          </div>
        )}

        {/* Main content */}
        <div className={`flex-1 min-w-0 overflow-x-hidden bg-gray-50 py-6 ${isDesktop ? 'px-20' : 'px-4'}`}>
          {renderStepContent()}
        </div>
      </div>
    </div>
  )
}

export function BulkOffboardingPage ({ isConnectedToPayroll, employerName, employerDba, employeeId, selectedEmployeeIds, showNavigation = true, isAle }: BulkOffboardingPageProps) {
  // When selectedEmployeeIds is provided, start at details step
  const initialStep = selectedEmployeeIds && selectedEmployeeIds.length > 0 ? 'details' : undefined

  return (
    <BulkOffboardingProvider
      isConnectedToPayroll={isConnectedToPayroll}
      employeeId={employeeId}
      initialSelectedIds={selectedEmployeeIds || undefined}
      initialStep={initialStep}
    >
      <BulkOffboardingContent employerName={employerName} employerDba={employerDba} showNavigation={showNavigation} isAle={isAle} />
    </BulkOffboardingProvider>
  )
}

export default BulkOffboardingPage
